import { __decorate } from "tslib";
import { AutoEncoder, field, MapDecoder, StringDecoder } from '@simonbackx/simple-encoding';
import { LoadedPermissions } from './LoadedPermissions.js';
import { PermissionLevel } from './PermissionLevel.js';
import { PermissionRoleDetailed } from './PermissionRole.js';
import { Permissions } from './Permissions.js';
import { PermissionsResourceType } from './PermissionsResourceType.js';
import { Platform } from './Platform.js';
export class UserPermissions extends AutoEncoder {
    constructor() {
        super(...arguments);
        this.globalPermissions = null;
        this.organizationPermissions = new Map();
    }
    // Current list of groups
    get platform() {
        return this.forPlatform(Platform.shared);
    }
    forPlatform(platform) {
        if (!this.globalPermissions) {
            return null;
        }
        const platformRoles = platform.getRoles();
        return LoadedPermissions.from(this.globalPermissions, platformRoles, [], platform.config.responsibilities);
    }
    forOrganization(organization, platform) {
        let base = null;
        if (platform) {
            const platformPermissions = this.forPlatform(platform);
            if (platformPermissions) {
                const tags = organization.meta.tags.length === 0 ? [''] : organization.meta.tags;
                for (const tag of tags) {
                    const rp = platformPermissions.getMergedResourcePermissions(PermissionsResourceType.OrganizationTags, tag);
                    if (rp) {
                        if (rp.hasAccess(PermissionLevel.Full)) {
                            const pp = Permissions.create({
                                level: rp.level,
                            });
                            return LoadedPermissions.from(pp, [], [], []);
                        }
                        base = base ? base.merge(rp) : rp;
                    }
                }
            }
        }
        const specific = this.forWithoutInherit(organization);
        if (base) {
            const roleWithBaseAccessRights = base.accessRights.length > 0
                ? [PermissionRoleDetailed.create({
                        accessRights: base.accessRights,
                    })]
                : [];
            const p = LoadedPermissions.from(Permissions.create({
                level: base.level,
                roles: roleWithBaseAccessRights,
            }), roleWithBaseAccessRights, [], []);
            if (!specific) {
                return p;
            }
            return specific.merge(p);
        }
        return specific;
    }
    forWithoutInherit(organization) {
        var _a, _b, _c, _d, _e, _f, _g;
        const permissions = (_a = this.organizationPermissions.get(organization.id)) !== null && _a !== void 0 ? _a : null;
        if (!permissions) {
            return null;
        }
        const organizationRoles = (_c = (_b = organization === null || organization === void 0 ? void 0 : organization.privateMeta) === null || _b === void 0 ? void 0 : _b.roles) !== null && _c !== void 0 ? _c : [];
        const inheritedResponsibilityRoles = (_e = (_d = organization === null || organization === void 0 ? void 0 : organization.privateMeta) === null || _d === void 0 ? void 0 : _d.inheritedResponsibilityRoles) !== null && _e !== void 0 ? _e : [];
        const allResponsibilities = [...Platform.shared.config.responsibilities, ...((_g = (_f = organization === null || organization === void 0 ? void 0 : organization.privateMeta) === null || _f === void 0 ? void 0 : _f.responsibilities) !== null && _g !== void 0 ? _g : [])];
        return LoadedPermissions.from(permissions, organizationRoles, inheritedResponsibilityRoles, allResponsibilities);
    }
    convertPlatformPatch(patch) {
        if (!this.globalPermissions) {
            const clonedPatch = UserPermissions.patch({
                globalPermissions: patch === null ? null : Permissions.create({}).patch(patch),
            });
            return clonedPatch;
        }
        return UserPermissions.patch({
            globalPermissions: patch,
        });
    }
    convertPatch(patch, organizationId) {
        if (!this.organizationPermissions.get(organizationId)) {
            const clonedPatch = UserPermissions.patch({});
            const p = patch === null ? null : Permissions.create({}).patch(patch);
            clonedPatch.organizationPermissions.set(organizationId, p);
            return clonedPatch;
        }
        const clonedPatch = UserPermissions.patch({});
        clonedPatch.organizationPermissions.set(organizationId, patch);
        return clonedPatch;
    }
    clearEmptyPermissions() {
        if (this.globalPermissions && this.globalPermissions.isEmpty) {
            this.globalPermissions = null;
        }
        for (const [organizationId, permissions] of this.organizationPermissions) {
            if (permissions.isEmpty) {
                this.organizationPermissions.delete(organizationId);
            }
        }
    }
    get isEmpty() {
        return this.globalPermissions === null && this.organizationPermissions.size === 0;
    }
    static limitedPatch(old, patch, organizationId) {
        if (patch === null) {
            return old;
        }
        if (patch.isPatch()) {
            // Only allow to set the permissions for the organization in scope
            if (patch.organizationPermissions.get(organizationId) !== undefined) {
                const clonedPatch = UserPermissions.patch({});
                clonedPatch.organizationPermissions.set(organizationId, patch.organizationPermissions.get(organizationId));
                const updated = old ? old.patch(clonedPatch) : UserPermissions.create({}).patch(clonedPatch);
                if (updated.isEmpty) {
                    return null;
                }
                return updated;
            }
        }
        else {
            // Only allow to set the permissions for the organization in scope
            if (patch.organizationPermissions.get(organizationId)) {
                const clonedPatch = UserPermissions.patch({});
                clonedPatch.organizationPermissions.set(organizationId, patch.organizationPermissions.get(organizationId));
                const updated = old ? old.patch(clonedPatch) : UserPermissions.create({}).patch(clonedPatch);
                if (updated.isEmpty) {
                    return null;
                }
                return updated;
            }
        }
        return old;
    }
    static limitedAdd(old, add, organizationId) {
        if (add.organizationPermissions.get(organizationId)) {
            const cloned = old ? old.clone() : UserPermissions.create({});
            const realAdd = add.organizationPermissions.get(organizationId);
            const realOld = cloned ? cloned.organizationPermissions.get(organizationId) : null;
            if (!realOld) {
                cloned.organizationPermissions.set(organizationId, realAdd);
                return cloned;
            }
            realOld.add(realAdd);
            return cloned;
        }
        return old;
    }
}
__decorate([
    field({ decoder: Permissions, nullable: true })
], UserPermissions.prototype, "globalPermissions", void 0);
__decorate([
    field({ decoder: new MapDecoder(StringDecoder, Permissions) })
], UserPermissions.prototype, "organizationPermissions", void 0);
//# sourceMappingURL=UserPermissions.js.map